/**
 * DeleteColumnDialog (FE-6)
 *
 * Dialog per eliminare una colonna con gestione task:
 * - Se vuota: semplice conferma DELETE
 * - Se piena: select destinazione + conferma move-and-delete
 *
 * Il parametro `force` è stato rimosso dal contratto BE.
 */
import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import type { TaskStatusDetail } from "../../utils/taskHelpers";

export interface DeleteColumnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnId?: number;
  columnName?: string;
  tasksCount: number;
  /** Colonne disponibili come destinazione (esclusa la colonna da eliminare) */
  availableColumns: TaskStatusDetail[];
  /** Callback per eliminazione colonna vuota (DELETE semplice) */
  onDeleteEmpty: (columnId: number) => Promise<{ success: boolean; error?: string }>;
  /** Callback per move-and-delete (colonna piena con destinazione) */
  onMoveAndDelete: (columnId: number, destinationId: number) => Promise<{ success: boolean; error?: string }>;
}

export default function DeleteColumnDialog({
  open,
  onOpenChange,
  columnId,
  columnName,
  tasksCount,
  availableColumns,
  onDeleteEmpty,
  onMoveAndDelete,
}: DeleteColumnDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [destinationId, setDestinationId] = useState<number | null>(null);

  // Reset allo stato iniziale quando cambia il dialog o la colonna
  useEffect(() => {
    if (!open) {
      setError(null);
      setDestinationId(null);
    } else {
      // Prefill: prima colonna disponibile
      if (tasksCount > 0 && availableColumns.length > 0) {
        setDestinationId(availableColumns[0].id);
      }
    }
  }, [open, tasksCount, availableColumns]);

  const isEmpty = tasksCount === 0;
  const isValidDestination = destinationId !== null && destinationId !== columnId;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (columnId === undefined || submitting) return;

    setError(null);
    setSubmitting(true);

    try {
      if (isEmpty) {
        // DELETE semplice per colonna vuota
        const result = await onDeleteEmpty(columnId);
        if (result.success) {
          onOpenChange(false);
        } else {
          setError(result.error || "Errore durante l'eliminazione");
        }
      } else {
        // move-and-delete per colonna piena
        if (destinationId === null) {
          setError("Seleziona una colonna di destinazione");
          setSubmitting(false);
          return;
        }
        const result = await onMoveAndDelete(columnId, destinationId);
        if (result.success) {
          onOpenChange(false);
        } else {
          setError(result.error || "Errore durante lo spostamento");
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <AlertDialogContent className="bg-[var(--hexa-surface)] border-[var(--hexa-border)]">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[var(--hexa-text-primary)]">
            Elimina colonna
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[var(--hexa-text-secondary)]">
            {isEmpty ? (
              <>
                Sei sicuro di voler eliminare la colonna <strong>"{columnName}"</strong>?
                Questa azione è irreversibile.
              </>
            ) : (
              <>
                La colonna <strong>"{columnName}"</strong> contiene <strong>{tasksCount} task</strong>.
                Seleziona una colonna di destinazione dove spostarli prima dell'eliminazione.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <form onSubmit={handleSubmit}>
          {!isEmpty && (
            <div className="py-2">
              <label className="block text-xs font-medium text-[var(--hexa-text-primary)] mb-2">
                Sposta i task nella colonna:
                <span className="text-[#ff6467]">*</span>
              </label>
              <select
                value={destinationId ?? ""}
                onChange={(e) => setDestinationId(e.target.value === "" ? null : Number(e.target.value))}
                disabled={submitting || availableColumns.length === 0}
                className="w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--hexa-purple)] transition disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label="Colonna di destinazione"
              >
                {availableColumns.length === 0 && (
                  <option value="">Nessuna colonna disponibile</option>
                )}
                {availableColumns.map((col) => (
                  <option key={col.id} value={col.id}>
                    {col.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <p className="text-xs text-[#ef4444] bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-md px-3 py-2">
              {error}
            </p>
          )}

          <AlertDialogFooter className="pt-4">
            <AlertDialogCancel
              disabled={submitting}
              className="bg-[var(--hexa-surface-elevated)] border border-[var(--hexa-border)] text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
            >
              Annulla
            </AlertDialogCancel>
            <Button
              type="submit"
              disabled={submitting || (!isEmpty && !isValidDestination)}
              className="bg-[#ef4444] hover:bg-[#dc2626] text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {submitting ? "Eliminazione..." : "Elimina colonna"}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
